import { useEffect, useState } from "react";
import { useMutation, useQuery, useReactiveVar } from "@apollo/client";
import mixpanel from "mixpanel-browser";

import {
  CREATE_CONNECT_DASHBOARD_LINK,
  CREATE_CONNECT_ONBOARDING_LINK,
  CREATE_PRO_BILLING_PORTAL_SESSION,
  GET_ORGANIZATION_BILLING_DETAILS,
} from "../../api/gqlQueries";
import {
  EConnectExpressOnboardingStatus,
  IOrganizationBillingDetailsData,
  IOrganizationParams,
} from "../../interfaces/interfaces";
import { organizationIdNameVar, smallHeaderTitle } from "../apollo/LocalState";
import Breadcrumbs from "../breadcrumbs/Breadcrumbs";
import { ReactComponent as ExternalLink } from "../../img/ExternalLink.svg";
import { ReactComponent as ProAndTenant } from "../../img/payments/pro-and-tenant.svg";
import { ReactComponent as Sparkle } from "../../img/Sparkle.svg";
import { ReactComponent as Tenants } from "../../img/Tenants.svg";

const PaymentsAndPayouts: React.FC = () => {
  const { organizationId, organizationName } = useReactiveVar(
    organizationIdNameVar
  );

  const [errorAction, setErrorAction] = useState<"payments" | "payouts" | null>(
    null
  );

  const crumbs = [
    {
      name: organizationName,
      path: `/${organizationId}/organization`,
    },
    { name: "Brilliant Max", path: "" },
  ];

  const [createProBillingPortalSession, { loading }] = useMutation(
    CREATE_PRO_BILLING_PORTAL_SESSION
  );

  const [createConnectDashboardLink, { loading: payoutsLoading }] = useMutation(
    CREATE_CONNECT_DASHBOARD_LINK
  );

  const [createConnectOnboardingLink, { loading: onboardingLoading }] =
    useMutation(CREATE_CONNECT_ONBOARDING_LINK);

  const { data: billingData } = useQuery<
    IOrganizationBillingDetailsData,
    IOrganizationParams
  >(GET_ORGANIZATION_BILLING_DETAILS, {
    skip: !organizationId,
    variables: { organizationId },
  });

  const isPayoutsOnboardingComplete =
    billingData?.organization.connectExpressOnboardingStatus ===
      EConnectExpressOnboardingStatus.OnboardingComplete ||
    billingData?.organization.connectExpressOnboardingStatus ===
      EConnectExpressOnboardingStatus.DetailsSubmitted;

  useEffect(() => {
    smallHeaderTitle("Brilliant Max");
    return () => {
      smallHeaderTitle("");
    };
  }, []);

  const handleManagePayments = async () => {
    // Open the tab synchronously inside the click handler so it isn't blocked
    // by the popup blocker, then point it at the URL once the mutation returns.
    mixpanel.track("max_payments_viewed", {
      organization_id: organizationId,
      organization_name: organizationName,
    });
    setErrorAction(null);
    const newTab = window.open("", "_blank");
    try {
      const { data } = await createProBillingPortalSession({
        variables: { orgId: organizationId },
      });
      const url = data?.createProBillingPortalSession?.portalUrl;
      if (newTab && url) {
        newTab.location.href = url;
      } else {
        newTab?.close();
        setErrorAction("payments");
      }
    } catch {
      newTab?.close();
      setErrorAction("payments");
    }
  };

  const handleManagePayouts = async () => {
    mixpanel.track("max_payouts_viewed", {
      organization_id: organizationId,
      organization_name: organizationName,
    });
    setErrorAction(null);
    const newTab = window.open("", "_blank");
    try {
      const { data } = await createConnectDashboardLink({
        variables: { orgId: organizationId },
      });
      const url = data?.createConnectDashboardLink?.dashboardUrl;
      if (newTab && url) {
        newTab.location.href = url;
      } else {
        newTab?.close();
        setErrorAction("payouts");
      }
    } catch {
      newTab?.close();
      setErrorAction("payouts");
    }
  };

  const handleSetUpPayouts = async () => {
    mixpanel.track("max_payouts_viewed", {
      organization_id: organizationId,
      organization_name: organizationName,
    });
    setErrorAction(null);
    const newTab = window.open("", "_blank");
    try {
      const { data } = await createConnectOnboardingLink({
        variables: { orgId: organizationId },
      });
      const url = data?.createConnectOnboardingLink?.onboardingUrl;
      if (newTab && url) {
        newTab.location.href = url;
      } else {
        newTab?.close();
        setErrorAction("payouts");
      }
    } catch {
      newTab?.close();
      setErrorAction("payouts");
    }
  };

  return (
    <div className="page-container center-align">
      <div className="page-container-inner padding-bottom-large">
        <Breadcrumbs
          crumbs={crumbs}
          rootCrumb={{ name: "Organizations", path: "/" }}
        />
        <div className="header unit-title payments-and-payouts-title">
          <Sparkle
            className="payments-and-payouts-header-icon"
            title="Brilliant Max"
          />
          Brilliant Max
        </div>
        <div className="margin-top-small" />
        <div className="payments-and-payouts-list">
          <div className="payments-and-payouts-section">
            <p className="menu">Manage Subscriptions</p>
            <div className="payments-and-payouts-section-content">
              <p>
                For Brilliant Max subscriptions that you have assigned to homes,
                you can view, cancel, and manage payment methods here.{" "}
                <button
                  className="payments-and-payouts-link"
                  disabled={loading}
                  onClick={handleManagePayments}
                  type="button"
                >
                  <span>Manage Subscriptions</span>
                  <ExternalLink />
                </button>
              </p>
              {errorAction === "payments" && (
                <p className="payments-and-payouts-error">
                  Something went wrong. Please try again.
                </p>
              )}
            </div>
          </div>
          <div className="payments-and-payouts-section">
            <p className="menu">Manage Payouts</p>
            <div className="payments-and-payouts-section-content">
              <p>
                To receive payouts for commissions on Brilliant Max
                subscriptions that homeowners have upgraded on their own,
                you&rsquo;ll need to connect your bank account to view and
                receive upcoming payouts.{" "}
                <button
                  className="payments-and-payouts-link"
                  disabled={
                    isPayoutsOnboardingComplete
                      ? payoutsLoading
                      : onboardingLoading
                  }
                  onClick={
                    isPayoutsOnboardingComplete
                      ? handleManagePayouts
                      : handleSetUpPayouts
                  }
                  type="button"
                >
                  <span>Manage Payouts</span>
                  <ExternalLink />
                </button>
              </p>
              {errorAction === "payouts" && (
                <p className="payments-and-payouts-error">
                  Something went wrong. Please try again.
                </p>
              )}
            </div>
          </div>
          <div className="payments-and-payouts-card">
            <p className="menu">How it works</p>
            <div className="payments-and-payouts-card-row">
              <Sparkle
                className="payments-and-payouts-card-icon"
                title="Two ways to earn"
              />
              <div className="payments-and-payouts-card-content">
                <p className="placeholder">
                  Two ways to earn: Pro-Managed and Self-Managed Subscriptions
                </p>
                <p>
                  You can buy and assign a Brilliant Max subscription to a home
                  in the home details page OR homeowners can upgrade on their
                  own later through the Brilliant Control or App.
                </p>
              </div>
            </div>
            <div className="payments-and-payouts-card-row">
              <ProAndTenant
                className="payments-and-payouts-card-icon payments-and-payouts-card-icon-pro-managed"
                title="Pro-Managed Subscriptions"
              />
              <div className="payments-and-payouts-card-content">
                <p className="placeholder">Pro-Managed Subscriptions</p>
                <p>
                  You buy an annual Brilliant Max subscription for $69.99 per
                  year, assign to the home, and bundle/sell to homeowners
                  however you like. You will pay Brilliant for these, and the
                  homeowner will pay you. The home will be upgraded until you
                  decide to cancel from the link above.
                </p>
              </div>
            </div>
            <div className="payments-and-payouts-card-row">
              <Tenants
                className="payments-and-payouts-card-icon"
                title="Self-Managed Subscriptions"
              />
              <div className="payments-and-payouts-card-content">
                <p className="placeholder">Self-Managed Subscriptions</p>
                <p>
                  If you decide not to sell Pro-Managed Subscriptions, or if the
                  home owner decides to upgrade later on their own, you&rsquo;ll
                  still receive a 20% commission of recurring subscription
                  revenue for as long as homeowner has an active subscription
                  AND the home is in your ProView account. The homeowner will
                  pay Brilliant for these and Brilliant will pay you your
                  commission. You can manage those payouts above.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentsAndPayouts;
